import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CountUp from 'react-countup';
import { File, User, UserCheck } from 'feather-icons-react/build/IconComponents';
import Chart from 'react-apexcharts';
import { Link } from 'react-router-dom';
import { ImageWithBasePath } from 'Core/components';
import { ArrowRight } from 'react-feather';
import { all_routes } from '../../router/all_routes';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { fetchDashboardData, resetDashboard, setDashboardDataDirectly } from './store/slice';
import { selectDashboardLoading, selectDashboardTiles, selectProcessDetails, selectDashboardError, selectDashboardData } from './store/selectors';
import { httpInterceptor } from '../../service/http-interceptor';
import { DashboardService } from '../../service/api/dashboard-service';

const Dashboard = () => {
  const dispatch = useDispatch();
  const tiles = useSelector(selectDashboardTiles);
  const processDetails = useSelector(selectProcessDetails);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const rawData = useSelector(selectDashboardData);
  
  // Local state as backup if Redux fails
  const [directData, setDirectData] = useState(null);
  const [completingWorkTicket, setCompletingWorkTicket] = useState('');
  
  // Debug logs
  console.log('Dashboard Render - Tiles:', tiles);
  console.log('Dashboard Render - Process Details:', processDetails);
  console.log('Dashboard Render - Raw Data:', rawData);
  
  useEffect(() => {
    // Reset dashboard state on unmount
    return () => {
      dispatch(resetDashboard());
    };
  }, [dispatch]);
  
  const loadDashboardData = useCallback(() => {
    console.log('Fetching dashboard data...');

    // Method 1: Redux async thunk
    dispatch(fetchDashboardData());

    // Method 2: Direct fetch via fetch API as backup
    httpInterceptor.get('Mobile/dashboarddata')
      .then(response => {
        console.log('Direct API fetch via interceptor:', response);
        if (response.status === 200 && response.response) {
          setDirectData(response.response);
          // Also dispatch directly to Redux
          dispatch(setDashboardDataDirectly(response.response));
        }
      })
      .catch(err => console.error('Direct API fetch error:', err));
  }, [dispatch]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleCompleteProcess = async (workTicketNumber) => {
    if (!workTicketNumber) return;

    try {
      setCompletingWorkTicket(workTicketNumber);
      const response = await DashboardService.completeProcess(workTicketNumber);
      const status = response?.status ?? response?.Status;

      if (status === 200 || status === 'OK') {
        await loadDashboardData();
        MySwal.fire({
          title: 'Completed!',
          text: 'Process completed successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'btn btn-success',
          },
        });
      }
    } catch (err) {
      console.error('Complete process error:', err);
    } finally {
      setCompletingWorkTicket('');
    }
  };

  // Use direct data if Redux data is empty
  const displayTiles = tiles.tile1 === 0 && directData ? {
    tile1: directData.tile1 || 0,
    tile2: directData.tile2 || 0,
    tile3: directData.tile3 || 0,
    tile4: directData.tile4 || 0,
    tile5: directData.tile5 || 0,
    tile6: directData.tile6 || 0,
    tile7: directData.tile7 || 0,
    tile8: directData.tile8 || 0,
  } : tiles;
  
  const displayProcessDetails = processDetails.length === 0 && directData ? 
    (directData.processDetails || []) : processDetails;

  const route = all_routes;
  const [chartOptions] = useState({
    series: [
      {
        name: 'Sales',
        data: [130, 210, 300, 290, 150, 50, 210, 280, 105],
      },
      {
        name: 'Purchase',
        data: [-150, -90, -50, -180, -50, -70, -100, -90, -105],
      },
    ],
    colors: ['#28C76F', '#EA5455'],
    chart: {
      type: 'bar',
      height: 320,
      stacked: true,
      zoom: {
        enabled: true,
      },
    },
    responsive: [
      {
        breakpoint: 280,
        options: {
          legend: {
            position: 'bottom',
            offsetY: 0,
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        borderRadiusApplication: 'end', // "around" / "end"
        borderRadiusWhenStacked: 'all', // "all"/"last"
        columnWidth: '20%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    yaxis: {
      min: -200,
      max: 300,
      tickAmount: 5,
    },
    xaxis: {
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
    },
    legend: { show: false },
    fill: {
      opacity: 1,
    },
  });
  const MySwal = withReactContent(Swal);
  const showConfirmationAlert = () => {
    MySwal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: '#00ff00',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#ff0000',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        MySwal.fire({
          title: 'Deleted!',
          text: 'Your file has been deleted.',
          className: 'btn btn-success',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'btn btn-success',
          },
        });
      } else {
        MySwal.close();
      }
    });
  };
  return (
    <div>
      {error && (
        <div className="alert alert-danger">
          Error loading dashboard data: {error}
        </div>
      )}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
             
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath src="assets/img/icons/dash1.svg" alt="img" />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    <CountUp start={0} end={displayTiles.tile1} duration={3} prefix="$" />
                  </h5>
                  <h6>Total Sales Today</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget dash1 w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath src="assets/img/icons/dash2.svg" alt="img" />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    $
                    <CountUp
                      start={0}
                      end={displayTiles.tile2}
                      duration={3} // Duration in seconds
                    />
                  </h5>
                  <h6>Total Sales This Month</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget dash2 w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath src="assets/img/icons/dash3.svg" alt="img" />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    <CountUp
                      start={0}
                      end={displayTiles.tile3}
                      duration={3} // Duration in seconds
                      decimals={1}
                    />
                  </h5>
                  <h6>Total Machines Running</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget dash3 w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath src="assets/img/icons/dash4.svg" alt="img" />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    <CountUp
                      start={0}
                      end={displayTiles.tile4}
                      duration={3} // Duration in seconds
                    />
                  </h5>
                  <h6>Total Quality Inspections Completed</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-count">
                <div className="dash-counts">
                  <h4>{displayTiles.tile5}</h4>
                  <h5>Total Machines Down</h5>
                </div>
                <div className="dash-imgs">
                  <User />
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-count das1">
                <div className="dash-counts">
                  <h4>{displayTiles.tile6}</h4>
                  <h5>Total Shipments for the Day</h5>
                </div>
                <div className="dash-imgs">
                  <UserCheck />
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-count das2">
                <div className="dash-counts">
                  <h4>{displayTiles.tile7}</h4>
                  <h5>Total Mold Maintenance Completed</h5>
                </div>
                <div className="dash-imgs">
                  <ImageWithBasePath
                    src="assets/img/icons/file-text-icon-01.svg"
                    className="img-fluid"
                    alt="icon"
                  />
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-count das3">
                <div className="dash-counts">
                  <h4>{displayTiles.tile8}</h4>
                  <h5>Total Machine Maintenance Completed</h5>
                </div>
                <div className="dash-imgs">
                  <File />
                </div>
              </div>
            </div>
          </div>
          {/* Button trigger modal */}

         
          
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Production Details</h4>
            </div>
            <div className="card-body">
              <div className="table-responsive dataview">
                <table className="table dashboard-table">
                  <thead>
                    <tr>
                     
                      <th>Cell</th> 
                       <th>Work Ticket#</th> 
                      <th>Status</th>
                      <th>Start</th>  
                      <th>Part </th>      
                      <th>Demand</th>                      
                      <th>Cavities</th>
                      <th>Cycle Time</th>
                      <th>Current Stock</th>
                      <th>% of 3 month demand</th>
                      <th>Production Time(Days)</th>
                      <th>Shot Weight</th>
                      <th>Est. End Date</th>                      
                      <th>Est. Mat Usage</th>
                      <th>Notes</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && !directData ? (
                      <tr>
                        <td colSpan="16" className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : displayProcessDetails && displayProcessDetails.length > 0 ? (
                      displayProcessDetails.map((item, index) => (
                        <tr key={index}>                         
                          <td>{item.cellNumber}</td>
                          <td>{item.workTicketNumber}</td>
                          <td>{item.status}</td>
                          <td>{item.fromDate ? new Date(item.fromDate).toISOString().split('T')[0] : ''}</td>
                          <td>{item.partNumber}</td>
                          <td>Demand</td>
                          <td>{item.moldCavityCount}</td>
                          <td>{item.cycleTime}</td>                          
                          <td>{item.currentStock}</td>
                          <td>%</td>
                          <td>{item.productionDays}</td>
                          <td>ShotWt</td>
                          <td>{item.toDate ? new Date(item.toDate).toISOString().split('T')[0] : ''}</td>
                          <td>Est. Mat Usage</td>
                          <td>{item.locationName}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={
                                completingWorkTicket === item.workTicketNumber ||
                                String(item.status || '').toLowerCase() === 'completed'
                              }
                              onClick={() => handleCompleteProcess(item.workTicketNumber)}
                            >
                              {completingWorkTicket === item.workTicketNumber ? 'Completing...' : 'Complete'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="16" className="text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
